import { useState } from 'react'
import { useReportBusy } from '@/console/state/useReportBusy'
import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { ConfirmDialog } from '@/console/ui/ConfirmDialog'
import { Field } from '@/console/ui/Field'
import { Note } from '@/console/ui/Note'
import { SectionHeading } from '@/console/ui/SectionHeading'
import { SourceBadge } from '@/console/ui/SourceBadge'
import { Stack } from '@/console/ui/Stack'
import {
  McisCutoverRollbackResendFlow,
  RESEND_REQUEST_KIND,
  ResendExecution,
  useMcisCutoverRollbackResendFlow,
} from './McisCutoverRollbackResendFlow'
import { ResendRowTable, ResponseSummary, RowStatusCounts } from './McisCutoverRollbackResendRows'
import {
  EXECUTION_BLOCKER_MESSAGE,
  MAX_OUTBOUND_RESULT_ERP_IDS,
  MAX_REASON_LENGTH,
  OUTCOME,
  REASON_PROBLEM_MESSAGE,
  RUNBOOK_URL,
  ResendJudgement,
  releasedCountMismatch,
} from './mcisCutoverRollbackResend'
import styles from './McisCutoverRollbackResend.module.css'

const IN_FLIGHT_LABEL = {
  [RESEND_REQUEST_KIND.JUDGING]: '판정 중…',
  [RESEND_REQUEST_KIND.EXECUTING]: '실행 중… 응답이 올 때까지 탭을 옮길 수 없어요.',
} as const

const Preconditions = (): JSX.Element => (
  <Callout tone="warn">
    <b>실행 전 확인</b>
    <ul className={styles.list}>
      <li>MCIS 의 SAP 송신을 껐고, 진행 중이던 송신이 끝났다.</li>
      <li>입력은 MCIS 가 미SENT 로 판정한 OIF_LO_GI.id 뿐이다. PISTAT 은 MCIS SENT 근거가 아니다.</li>
      <li>MOMS 컷오버 flag(feature.erpResult.mcisCutover)가 꺼져 있다.</li>
    </ul>
    <a href={RUNBOOK_URL} target="_blank" rel="noreferrer">
      런북 열기 (mcis-cutover-rollback-resend.md)
    </a>
  </Callout>
)

/** 유효하지 않은 값이 섞이면 판정을 막는다 — 일부만 판정하면 담당자는 전부 확인했다고 믿게 된다. */
const ResendInputFields = ({ flow }: { flow: McisCutoverRollbackResendFlow }): JSX.Element => {
  const { parsed, reasonProblem, inFlight } = flow
  const showReasonProblem = reasonProblem !== null && flow.reasonText.length > 0

  return (
    <Stack>
      <Field label="OIF_LO_GI.id (쉼표·줄바꿈 구분)">
        <textarea
          rows={5}
          placeholder={'123456789\n123456790'}
          spellCheck={false}
          value={flow.idsText}
          disabled={inFlight !== null}
          onChange={(event) => flow.setIdsText(event.target.value)}
        />
      </Field>
      <div className={styles.meta}>
        유효 {parsed.ids.length}건 · 중복 제외 {parsed.duplicates}건 · 최대 {MAX_OUTBOUND_RESULT_ERP_IDS}건
      </div>
      {parsed.overLimit ? (
        <Callout tone="crit">중복을 뺀 뒤 최대 {MAX_OUTBOUND_RESULT_ERP_IDS}건까지 판정할 수 있어요. 나눠서 판정하세요.</Callout>
      ) : null}
      {parsed.invalid.length > 0 ? <Callout tone="crit">양의 정수가 아닌 값이 있어요: {parsed.invalid.join(', ')}</Callout> : null}
      <Field label={`사유 (감사 로그에 남아요, 최대 ${MAX_REASON_LENGTH}자)`}>
        <input
          type="text"
          placeholder="MCIS 재대사 결과 미SENT · 온콜 티켓 번호"
          value={flow.reasonText}
          disabled={inFlight !== null}
          onChange={(event) => flow.setReasonText(event.target.value)}
        />
      </Field>
      {showReasonProblem ? <Callout tone="crit">{REASON_PROBLEM_MESSAGE[reasonProblem]}</Callout> : null}
      <div>
        <Button disabled={flow.currentInput === null || inFlight !== null} onClick={flow.judge}>
          판정 (dryRun)
        </Button>
      </div>
    </Stack>
  )
}

const JudgementResult = ({ flow, judgement }: { flow: McisCutoverRollbackResendFlow; judgement: ResendJudgement }): JSX.Element => (
  <Stack>
    <SectionHeading level={3} title="판정 결과" count={`${judgement.response.rows.length}건`} />
    <ResponseSummary response={judgement.response} />
    <RowStatusCounts rows={judgement.response.rows} />
    {(flow.executionBlockers ?? [])
      .filter((blocker) => blocker !== 'IN_FLIGHT')
      .map((blocker) => (
        <Callout key={blocker} tone={blocker === 'STALE' ? 'warn' : 'crit'}>
          {EXECUTION_BLOCKER_MESSAGE[blocker]}
        </Callout>
      ))}
    <ResendRowTable rows={judgement.response.rows} />
  </Stack>
)

const ExecutionDialogBody = ({ judgement }: { judgement: ResendJudgement }): JSX.Element => (
  <>
    <p className={styles.dialogLead}>
      요청 {judgement.input.outboundResultErpIds.length}건 중 실행 대상 {judgement.response.eligibleCount}건의 API_FLAG 를 1 에서 0 으로 되돌려요.
    </p>
    <ul className={styles.list}>
      <li>API_FLAG=1 이고 ORD_REMARK=MCIS_CUTOVER 인 행만 바뀌어요. 서버가 실행 순간에 다시 확인해요.</li>
      <li>SAP 를 직접 부르지 않아요. 기존 배치가 다음 주기에 SAP 로 보내요.</li>
      <li>MCIS 송신 OFF · 진행 중 송신 종료 · MOMS 컷오버 flag OFF 를 확인했어요.</li>
    </ul>
    <p className={styles.dialogReason}>사유: {judgement.input.reason}</p>
  </>
)

/** 실행 대상은 판정에 쓴 id 집합 전체와 사유 그대로다. 행을 골라 일부만 실행하는 길은 없다. */
const ExecuteAction = ({ flow }: { flow: McisCutoverRollbackResendFlow }): JSX.Element | null => {
  const [pendingConfirm, setPendingConfirm] = useState(false)
  const { judgement } = flow
  if (!judgement) return null

  const confirmExecution = (): void => {
    setPendingConfirm(false)
    flow.execute()
  }

  return (
    <div>
      <Button disabled={!flow.canExecute} onClick={() => setPendingConfirm(true)}>
        실행 ({judgement.response.eligibleCount}건 복원)
      </Button>
      <ConfirmDialog
        open={pendingConfirm}
        title="API_FLAG 복원을 실행할까요?"
        body={<ExecutionDialogBody judgement={judgement} />}
        confirmLabel="실행"
        danger
        onConfirm={confirmExecution}
        onCancel={() => setPendingConfirm(false)}
      />
    </div>
  )
}

const ExecutionOutcomeGuide = ({ execution }: { execution: ResendExecution }): JSX.Element | null => {
  const { response } = execution
  if (!response) return null
  if (response.outcome === OUTCOME.REJECTED_CUTOVER_ENABLED) {
    return <Callout tone="crit">MOMS 컷오버 flag 가 켜져 있어 거절했어요. 바뀐 행은 0건이에요. flag 를 끈 뒤 다시 판정하세요.</Callout>
  }
  if (response.outcome === OUTCOME.NO_OP) {
    return <Callout tone="warn">바뀐 행이 없어요. 이미 처리됐거나 실행 순간에 조건이 사라졌어요. 같은 목록으로 다시 판정해 확인하세요.</Callout>
  }
  if (releasedCountMismatch(response)) {
    return (
      <Callout tone="warn">
        판정 때 실행 대상 {response.eligibleCount}건 중 {response.releasedCount}건만 바뀌었어요. 그 사이 다른 경로가 행을 바꿨을 수 있어요. 다시
        판정해 확인하세요.
      </Callout>
    )
  }
  return <Callout tone="good">{response.releasedCount}건을 복원했어요. 같은 목록으로 다시 판정하면 이미 복원됨으로 보여요.</Callout>
}

/** 실행 결과. 실행 응답의 행은 갱신 전 분류라 "실행 시점 판정" 으로 보여 준다. */
const ExecutionResult = ({ execution }: { execution: ResendExecution | null }): JSX.Element | null => {
  if (!execution) return null

  return (
    <Stack>
      <SectionHeading level={3} title="실행 결과" count={`요청 ${execution.input.outboundResultErpIds.length}건`} />
      {execution.error ? <Callout tone="crit">{execution.error.message}</Callout> : null}
      <ExecutionOutcomeGuide execution={execution} />
      {execution.response ? (
        <>
          <ResponseSummary response={execution.response} />
          <SectionHeading level={3} title="실행 시점 판정" count={`${execution.response.rows.length}건`} />
          <ResendRowTable rows={execution.response.rows} />
        </>
      ) : null}
    </Stack>
  )
}

/**
 * MCIS 컷오버 롤백 재송신(`s-erp-mcis-rollback-resend`). MCIS 가 SAP 로 보내지 못한 출고실적을 MOMS 배치가
 * 다시 보내게 OIF_LO_GI.API_FLAG 를 0 으로 되돌린다. 실행 중에만 셸 탭을 잠근다 — 응답을 놓친 채 탭을 옮겼다
 * 돌아와 다시 누르는 일을 막는다. 판정은 아무것도 바꾸지 않아 잠그지 않는다.
 */
export const McisCutoverRollbackResendPanel = ({ onBusyChange }: { onBusyChange?: (busy: boolean) => void }): JSX.Element => {
  const flow = useMcisCutoverRollbackResendFlow()
  useReportBusy(flow.inFlight === RESEND_REQUEST_KIND.EXECUTING, onBusyChange)

  return (
    <section>
      <SectionHeading
        id="s-erp-mcis-rollback-resend"
        title="MCIS 컷오버 롤백 재송신"
        badge={<SourceBadge tone="new">신규 · POST /outbound-result-erp/mcis-cutover/rollback-resend</SourceBadge>}
      />
      <Stack>
        <Preconditions />
        <ResendInputFields flow={flow} />
        {flow.inFlight ? <Callout>{IN_FLIGHT_LABEL[flow.inFlight]}</Callout> : null}
        {flow.judgeError ? <Callout tone="crit">{flow.judgeError.message}</Callout> : null}
        {flow.judgement ? <JudgementResult flow={flow} judgement={flow.judgement} /> : null}
        <ExecuteAction flow={flow} />
        <ExecutionResult execution={flow.execution} />
      </Stack>
      <Note>
        판정은 아무것도 바꾸지 않아요. 실행은 판정과 같은 id·사유로만 나가고 자동으로 다시 보내지 않아요. <b>MOMS_ENG</b> 권한 필요.
      </Note>
    </section>
  )
}
